import {
    Box,
    TextField,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Typography,
    Collapse,
    InputAdornment,
} from "@mui/material";
import {
    Folder as FolderIcon,
    Description as FileIcon,
    ExpandLess,
    ExpandMore,
    Search as SearchIcon,
    Add as AddIcon,
    ContentCopy as DuplicateIcon,
} from "@mui/icons-material";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { getPrompts, getPromptContent } from "../../redux/reducer/prompts";
import { Prompt } from "../../interface/prompts";
import Button from "../atoms/Button";

interface PromptListProps {
    onCreateNew: () => void;
    onDuplicate: () => void;
    selectedPromptName: string | null;
}

const PromptList = ({
    onCreateNew,
    onDuplicate,
    selectedPromptName,
}: PromptListProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const prompts = useSelector<RootState, Prompt[]>(({ prompts: p }) => p.prompts);
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['prompts/GET_PROMPTS']);

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['extractor']));

    useEffect(() => {
        dispatch(getPrompts({}));
    }, [dispatch]);

    const filteredPrompts = useMemo(() => {
        if (!searchQuery) return prompts;
        const query = searchQuery.toLowerCase();
        return prompts.filter((p) => p.displayName.toLowerCase().includes(query)
            || p.name.toLowerCase().includes(query));
    }, [prompts, searchQuery]);

    const groupedPrompts = useMemo(() => {
        const groups: { [folder: string]: Prompt[] } = {
            root: [],
        };

        filteredPrompts.forEach((prompt) => {
            const folder = prompt.folder || 'root';
            if (!groups[folder]) {
                groups[folder] = [];
            }
            groups[folder].push(prompt);
        });

        return groups;
    }, [filteredPrompts]);

    const handleFolderClick = (folder: string) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folder)) {
                next.delete(folder);
            } else {
                next.add(folder);
            }
            return next;
        });
    };

    const handlePromptClick = (prompt: Prompt) => {
        dispatch(getPromptContent({
            input: { promptName: prompt.name },
        }));
    };

    if (loading && prompts.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const folders = Object.keys(groupedPrompts).filter((f) => f !== 'root').sort();

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
                <TextField
                    placeholder="Search prompts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <List dense>
                    {/* Folders */}
                    {folders.map((folder) => (
                        <Box key={folder}>
                            <ListItemButton onClick={() => handleFolderClick(folder)}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <FolderIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={folder} />
                                {expandedFolders.has(folder) ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={expandedFolders.has(folder)} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding dense>
                                    {groupedPrompts[folder].map((prompt) => (
                                        <ListItemButton
                                            key={prompt.name}
                                            sx={{ pl: 4 }}
                                            selected={selectedPromptName === prompt.name}
                                            onClick={() => handlePromptClick(prompt)}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <FileIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={prompt.displayName}
                                                secondary={new Date(prompt.lastModified).toLocaleDateString()}
                                                slotProps={{
                                                    primary: { component: 'div' },
                                                    secondary: { component: 'div' },
                                                }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Collapse>
                        </Box>
                    ))}

                    {/* Root level files */}
                    {groupedPrompts.root && groupedPrompts.root.length > 0 && (
                        <>
                            {folders.length > 0 && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ px: 2, py: 1, display: 'block' }}
                                >
                                    Root
                                </Typography>
                            )}
                            {groupedPrompts.root.map((prompt) => (
                                <ListItemButton
                                    key={prompt.name}
                                    selected={selectedPromptName === prompt.name}
                                    onClick={() => handlePromptClick(prompt)}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <FileIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={prompt.displayName}
                                        secondary={new Date(prompt.lastModified).toLocaleDateString()}
                                        slotProps={{
                                            primary: { component: 'div' },
                                            secondary: { component: 'div' },
                                        }}
                                    />
                                </ListItemButton>
                            ))}
                        </>
                    )}

                    {filteredPrompts.length === 0 && (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                {searchQuery ? 'No prompts found' : 'No prompts available'}
                            </Typography>
                        </Box>
                    )}
                </List>
            </Box>

            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={onCreateNew}
                    sx={{ flex: 1 }}
                >
                    New
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<DuplicateIcon />}
                    onClick={onDuplicate}
                    disabled={!selectedPromptName}
                    sx={{ flex: 1 }}
                >
                    Duplicate
                </Button>
            </Box>
        </Box>
    );
};

export default PromptList;
